import { BarChart3 } from "lucide-react";
import { useEffect, useRef } from "react";
import type ReactECharts from "echarts-for-react";
import Bar3DChart from "../../charts/Bar3DChart";
import Funnel3DChart from "../../charts/Funnel3DChart";
import { IDashboardFilterData, PipelineDepartmentData, PipelineOverviewData, PipelineSectorData } from "../../core/_models";
import { Link } from "react-router-dom";
import { encodeBase64 } from "../../../../utils/config";
import MapView from "./MapView";
import ChartExportMenu from "../../charts/ChartExportMenu";

type Props = {
  series: PipelineOverviewData[];
  sectorChartData: PipelineSectorData;
  departmentChartData: PipelineDepartmentData;
  filters: IDashboardFilterData;
  loading?: boolean;
};

const projectLink = (label: string, filters: IDashboardFilterData): string =>
  `/projects/?label=${encodeBase64(label)}&dashboard=${encodeBase64(
    "Pipeline Status"
  )}&url=${encodeBase64("dashboard")}&filter=${encodeBase64(JSON.stringify(filters))}`;

const PipeLineStatusCharts: React.FC<Props> = ({
  series,
  sectorChartData,
  departmentChartData,
  filters,
  loading = false,
}) => {
  const chartRef = useRef<ReactECharts>(null);
  const funnelRef = useRef<HTMLDivElement>(null);
  const canExportFunnel = series.length > 0;
  const maxDepartment = Math.max(...departmentChartData.values, 0);

  const handleProjectClick = () => {
    sessionStorage.setItem("projectScroll", window.scrollY.toString());
  };

  useEffect(() => {
    const savedScroll = sessionStorage.getItem("projectScroll");
    if (!savedScroll) return;
    if ([sectorChartData.categories, sectorChartData.values, sectorChartData.colors].every((arr) => arr.length)) {
      const timer = window.setTimeout(() => {
        window.scrollTo({
          top: parseInt(savedScroll, 10),
          behavior: "instant",
        });
        sessionStorage.removeItem("projectScroll");
        sessionStorage.removeItem("lastActiveTab");
      }, 100);

      return () => window.clearTimeout(timer);
    }
  }, [sectorChartData]);

  return (
    <div className="pipeline-viz">
      <div className="pipeline-viz__header">
        <div className="pipeline-viz__icon">
          <BarChart3 size={18} aria-hidden />
        </div>
        <div className="pipeline-viz__heading">
          <h2>Visualisation Summary</h2>
          <p>Comprehensive project pipeline insights</p>
        </div>
      </div>

      <div className="pipeline-viz__grid">
        <section className="pipeline-panel pipeline-panel--map">
          <h3>Map</h3>
          <div className="pipeline-map">
            <MapView filters={filters} height="440px" />
          </div>
        </section>

        <section className="pipeline-panel">
          <h3>Top departments</h3>
          {loading ? (
            <div>
              {Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className="pipeline-skeleton pipeline-skeleton--row" />
              ))}
            </div>
          ) : departmentChartData.names.length === 0 ? (
            <p className="pipeline-empty">No department data available.</p>
          ) : (
            <div className="pipeline-departments">
              {departmentChartData.names.map((name, index) => {
                const count = departmentChartData.values[index] ?? 0;
                const share = maxDepartment === 0 ? 0 : (count / maxDepartment) * 100;

                return (
                  <div key={`${name}-${index}`}>
                    <div className="pipeline-department__row">
                      <span className="pipeline-department__name" title={name}>{name}</span>
                      <span className="pipeline-department__count">{count.toLocaleString()}</span>
                    </div>
                    <div className="pipeline-department__track">
                      <div className="pipeline-department__bar" style={{ width: `${share}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <section className="pipeline-panel">
          <div className="pipeline-panel__head">
            <h3>Projects by sector</h3>
            <ChartExportMenu
              fileName="pipeline-projects-by-sector"
              disabled={loading || sectorChartData.values.length === 0}
              getChart={() => chartRef.current?.getEchartsInstance()}
            />
          </div>
          {loading ? (
            <div className="pipeline-skeleton" />
          ) : sectorChartData.categories.length === 0 ? (
            <p className="pipeline-empty">No sector data available.</p>
          ) : (
            <Bar3DChart ref={chartRef} chartData={sectorChartData} height={460} />
          )}
        </section>

        <section className="pipeline-panel">
          <div className="pipeline-panel__head">
            <h3>Sector mix</h3>
            <ChartExportMenu
              fileName="pipeline-sector-mix"
              disabled={!canExportFunnel || loading}
              getElement={() => funnelRef.current}
            />
          </div>
          {loading ? (
            <div className="pipeline-skeleton" />
          ) : series.length === 0 ? (
            <p className="pipeline-empty">No pipeline mix data available.</p>
          ) : (
            <>
              <Funnel3DChart ref={funnelRef} data={series} />
              <div className="pipeline-legend">
                {series.map(([label, , color], index) => (
                  <Link
                    key={`${label}-${index}`}
                    onClick={handleProjectClick}
                    to={index === 0 ? "#" : projectLink(label, filters)}
                  >
                    <i style={{ backgroundColor: color }} />
                    <span>{label}</span>
                  </Link>
                ))}
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
};

export { PipeLineStatusCharts };
